import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { CheckboxModule } from 'primeng/checkbox';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../../service/auth.service';
import { CryptoService } from '../../../service/crypto.service';

/**
 * KX-002/KX-017 Faz3 — Akış A: giriş sonrası kurtarma kodu kurulumu.
 *
 * Private key bu oturumda bellekteyse (login sırasında .key ile açıldıysa),
 * kullanıcıdan kullanıcı adı + parola alır (v2 paketleme için gerekli —
 * PBKDF2(username+password) mevcut login akışıyla birebir aynı olmalı),
 * yeni bir kurtarma kodu üretir, aynı private key'i hem parola hem kod ile
 * açılabilen bir v2 .key dosyasına paketler ve indirir. Kod bir kez ekranda
 * gösterilir; kullanıcı "sakladım" onayı vermeden devam edemez. Kod ve
 * private key hiçbir zaman sunucuya gönderilmez — tüm işlem istemcide.
 */
@Component({
    selector: 'app-recovery-setup',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        RouterModule,
        ButtonModule,
        InputTextModule,
        PasswordModule,
        CheckboxModule,
        ToastModule,
        TranslateModule
    ],
    providers: [MessageService],
    template: `
        <div class="flex items-center justify-center min-h-screen p-4 bg-surface-50 dark:bg-surface-950">
            <div class="card w-full" style="max-width: 32rem;">
                <h2>{{ 'COMMON.RECOVERY.SETUP_TITLE' | translate }}</h2>

                <ng-container *ngIf="!hasKey">
                    <div class="flex items-start gap-2 p-3 rounded border border-yellow-400/60 bg-yellow-500/10 text-sm mb-4">
                        <i class="pi pi-exclamation-triangle text-yellow-600 mt-0.5"></i>
                        <span>{{ 'COMMON.RECOVERY.NO_KEY_LOADED' | translate }}</span>
                    </div>
                    <p-button [label]="'COMMON.RECOVERY.CONTINUE' | translate" icon="pi pi-arrow-left" (onClick)="router.navigate([''])"></p-button>
                </ng-container>

                <!-- re-review minor 6: PQ-only oturumda (RSA yok) paketlenecek RSA anahtari yok; notr bilgi. -->
                <ng-container *ngIf="pqOnly">
                    <div class="flex items-start gap-2 p-3 rounded border border-surface-300 bg-surface-100 dark:bg-surface-800 text-sm mb-4">
                        <i class="pi pi-info-circle mt-0.5"></i>
                        <span>{{ 'COMMON.RECOVERY.PQ_ONLY_INFO' | translate }}</span>
                    </div>
                    <p-button [label]="'COMMON.RECOVERY.CONTINUE' | translate" icon="pi pi-arrow-left" (onClick)="router.navigate([''])"></p-button>
                </ng-container>

                <ng-container *ngIf="hasKey && !pqOnly">
                    <div *ngIf="mandatory && !code" class="flex items-start gap-2 p-3 rounded border border-red-400/60 bg-red-500/10 text-sm mb-4">
                        <i class="pi pi-lock text-red-600 mt-0.5"></i>
                        <span>{{ 'COMMON.RECOVERY.MANDATORY_INTRO' | translate }}</span>
                    </div>

                    <p class="text-sm text-surface-600 mb-4">{{ 'COMMON.RECOVERY.SETUP_INTRO' | translate }}</p>

                    <ng-container *ngIf="!code">
                        <p class="text-sm text-surface-600 mb-3">{{ 'COMMON.RECOVERY.NEED_PASSWORD' | translate }}</p>

                        <div class="p-field mb-3">
                            <label for="rsUsername">{{ 'COMMON.USERNAME' | translate }}</label>
                            <input id="rsUsername" pInputText type="text" class="w-full" [(ngModel)]="username" name="rsUsername" autocomplete="username" />
                        </div>

                        <div class="p-field mb-4">
                            <label for="rsPassword">{{ 'COMMON.PASSWORD' | translate }}</label>
                            <p-password
                                inputId="rsPassword"
                                [(ngModel)]="password"
                                name="rsPassword"
                                [toggleMask]="true"
                                [feedback]="false"
                                [fluid]="true"
                                autocomplete="current-password"
                            ></p-password>
                        </div>

                        <p-button
                            [label]="'COMMON.RECOVERY.GENERATE' | translate"
                            icon="pi pi-shield"
                            [loading]="generating"
                            [disabled]="generating || !username.trim() || !password.trim()"
                            (onClick)="generate()"
                        ></p-button>
                    </ng-container>

                    <ng-container *ngIf="code">
                        <div class="p-4 rounded border border-primary/40 bg-primary-50 dark:bg-primary-900/20 mb-4">
                            <div class="text-sm text-surface-500 mb-1">{{ 'COMMON.RECOVERY.YOUR_CODE' | translate }}</div>
                            <div class="font-mono text-lg font-semibold tracking-wide break-all">{{ code }}</div>
                            <div class="flex flex-wrap gap-2 mt-3">
                                <p-button [label]="'COMMON.RECOVERY.COPY_CODE' | translate" icon="pi pi-copy" size="small" text (onClick)="copyCode()"></p-button>
                                <p-button [label]="'COMMON.RECOVERY.DOWNLOAD_KEY' | translate" icon="pi pi-download" size="small" text (onClick)="redownload()"></p-button>
                            </div>
                        </div>

                        <div class="flex items-start gap-2 p-3 rounded border border-yellow-400/60 bg-yellow-500/10 text-sm mb-4">
                            <i class="pi pi-exclamation-triangle text-yellow-600 mt-0.5"></i>
                            <span>{{ 'COMMON.RECOVERY.SAVE_WARNING' | translate }}</span>
                        </div>

                        <div class="flex items-center gap-2 mb-4">
                            <p-checkbox [(ngModel)]="saved" [binary]="true" inputId="rsSaved" name="rsSaved"></p-checkbox>
                            <label for="rsSaved" class="cursor-pointer text-sm">{{ 'COMMON.RECOVERY.CONFIRM_SAVED' | translate }}</label>
                        </div>

                        <p-button [label]="'COMMON.RECOVERY.CONTINUE' | translate" icon="pi pi-check" [disabled]="!saved" (onClick)="finish()"></p-button>
                    </ng-container>

                    <!-- KX I-1: Zorunlu geldiğinde korunan sayfaya kaçış yoktur; tek çıkış logout'tur. -->
                    <div *ngIf="mandatory" class="mt-6 pt-4 border-t border-surface-200 dark:border-surface-700">
                        <p-button [label]="'COMMON.LOGOUT' | translate" icon="pi pi-sign-out" severity="secondary" [text]="true" size="small" (onClick)="logout()"></p-button>
                    </div>
                </ng-container>
            </div>
        </div>
        <p-toast position="bottom-center"></p-toast>
    `
})
export class RecoverySetupComponent {
    private auth = inject(AuthService);
    private crypto = inject(CryptoService);
    private messageService = inject(MessageService);
    translateService = inject(TranslateService);
    router = inject(Router);

    // Bu adım yalnız private key oturumda bellekteyken (login sırasında .key ile
    // açıldığında) anlamlıdır; aksi halde paketlenecek bir PEM yok.
    hasKey = this.auth.hasPrivateKey();

    // 14c v5 T2 re-review minor 6: anahtar var ama RSA yok (mobil yeni hesap, v5 PQ-only yedek).
    // Kurtarma kodu (v2 .key) yalniz RSA PEM paketler; bu oturum icin notr bilgi gosterilir.
    pqOnly = this.hasKey && !this.auth.hasRsaKey();

    // KX I-1: v1 .key ile giriş yapılmış ve kurtarma kodu henüz üretilmemiş (kalıcı
    // bayrak yok) durumunu işaretler. Kurulum artık opsiyoneldir — hiçbir guard bu
    // duruma göre yönlendirme yapmaz; True ise bu ekranda bir uyarı mesajı ve
    // logout kaçış yolu gösterilir.
    mandatory = this.hasKey
        && !this.pqOnly
        && this.auth.getKeyFileVersion() === 1
        && !this.isRecoverySetupDone();

    username = '';
    password = '';
    code: string | null = null;
    saved = false;
    generating = false;

    private lastKeyBytes: Uint8Array | null = null;

    generate() {
        const pem = this.auth.getPrivateKey();
        const username = this.username.trim();
        const password = this.password.trim();
        if (!pem) {
            // re-review minor 6: sessiz kalma -- RSA'siz oturumda paketlenecek anahtar yok.
            // Anahtar PIN korumali KarteX yedeginde; kullaniciya notr bilgi verilir, olay loglanir.
            console.info('RecoverySetupPqOnly');
            this.messageService.add({
                severity: 'info',
                summary: this.translateService.instant('COMMON.INFO'),
                detail: this.translateService.instant('COMMON.RECOVERY.PQ_ONLY_INFO')
            });
            return;
        }
        // Dugme bos alanlarda devre disidir; buraya yalniz programatik cagriyla gelinir.
        if (!username || !password) {
            return;
        }

        this.generating = true;
        try {
            const code = this.crypto.generateRecoveryCode();
            const bytes = this.crypto.packKeyFileV2(pem, username, password, code);
            this.lastKeyBytes = bytes;
            this.downloadKey(bytes);
            this.code = code;

            // KX I-1: Kullanıcı artık kurtarma kodlu (v2) bir key'e sahip — bu oturumda
            // zorunlu guard'ı serbest bırak. (Kalıcı bayrak yalnız "sakladım" + devam ile,
            // finish()'te yazılır; kod üretmek tek başına kalıcı kurulumu tamamlamaz.)
            this.auth.setKeyFileVersion(2);

            this.messageService.add({
                severity: 'success',
                summary: this.translateService.instant('COMMON.SUCCESS'),
                detail: this.translateService.instant('COMMON.RECOVERY.SETUP_SUCCESS')
            });
        } catch (error) {
            this.messageService.add({
                severity: 'error',
                summary: this.translateService.instant('COMMON.ERROR'),
                detail: this.translateService.instant('COMMON.RECOVERY.SETUP_FAILED')
            });
        } finally {
            // Hassas veriyi formdan temizle; kod (gizli olsa da tek seferlik gösterim
            // amaçlı) ekranda kalır ki kullanıcı kopyalayabilsin.
            this.password = '';
            this.generating = false;
        }
    }

    redownload() {
        if (this.lastKeyBytes) {
            this.downloadKey(this.lastKeyBytes);
        }
    }

    private downloadKey(bytes: Uint8Array) {
        const blob = new Blob([bytes], { type: 'application/octet-stream' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = 'kartex.key';
        document.body.appendChild(a);
        a.click();
        URL.revokeObjectURL(url);
        document.body.removeChild(a);
    }

    copyCode() {
        if (!this.code) {
            return;
        }
        navigator.clipboard.writeText(this.code).then(() => {
            this.messageService.add({
                severity: 'success',
                summary: this.translateService.instant('COMMON.SUCCESS'),
                detail: this.translateService.instant('COMMON.RECOVERY.CODE_COPIED'),
                life: 2000
            });
        }).catch(() => {
            // Pano API'si kullanılamıyor/izin yok: kod ekranda görünür — sessizce yut.
        });
    }

    finish() {
        if (!this.saved) {
            return;
        }
        // KX I-1: "sakladım" onayı + devam → kullanıcı-bazlı KALICI kurulum bayrağı yaz
        // (bu tarayıcıda, bu kullanıcı için ekranın uyarı durumunu bir daha göstermemek amacıyla).
        const userId = this.auth.getUserId();
        if (userId) {
            localStorage.setItem('kartex_recovery_setup_' + userId, '1');
        }
        // Emniyet: kod üretilmeden buraya gelinmiş olsa bile guard'ı bu oturumda serbest bırak.
        this.auth.setKeyFileVersion(2);
        this.router.navigate(['']);
    }

    // KX I-1: Zorunlu ekrandan tek meşru kaçış yolu — kurulumu yapmak istemeyen kullanıcı
    // çıkış yapabilir (korunan sayfaya sızmadan). Guard, tekrar giriş yapıldığında yeniden değerlendirir.
    logout() {
        this.auth.logout();
        this.router.navigate(['/auth/login']);
    }

    // KX I-1: Bu kullanıcı için kurtarma kodu kurulumu daha önce tamamlandı mı (kalıcı bayrak)?
    private isRecoverySetupDone(): boolean {
        const userId = this.auth.getUserId();
        return !!userId && localStorage.getItem('kartex_recovery_setup_' + userId) === '1';
    }
}
